import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleTemplate from '../../components/BlogArticleTemplate';

const FixedVsVariable = () => (
  <BlogArticleTemplate
    title="Fixed vs Variable Home Loans: Which Is Right for You?"
    description="A comprehensive comparison of fixed and variable rate home loans in Australia, including split loan options and how to choose based on your circumstances."
    path="/blog/fixed-vs-variable"
    author="Manage Your Loans Team"
    date="2026-02-28"
    readTime={11}
    faqs={[
      {
        q: 'What is the difference between a fixed and variable home loan?',
        a: 'A fixed rate home loan locks in your interest rate for a set period (typically one to five years), meaning your repayments stay the same regardless of market movements. A variable rate loan fluctuates with the market — your rate and repayments can go up or down as the lender adjusts their rates, usually in response to Reserve Bank of Australia (RBA) cash rate decisions. Each type has distinct advantages depending on your financial situation and risk tolerance.',
      },
      {
        q: 'Can I split my home loan between fixed and variable?',
        a: 'Yes, a split loan allows you to fix a portion of your loan while keeping the remainder on a variable rate. This gives you the repayment certainty of a fixed rate on part of your balance while retaining the flexibility (such as offset accounts and unlimited extra repayments) on the variable portion. The split ratio is flexible — common arrangements include 50/50, 60/40, or 70/30.',
      },
      {
        q: 'What happens when my fixed rate period ends?',
        a: 'When your fixed rate term expires, your loan automatically reverts to the lender\'s standard variable rate, which is often higher than the discounted rates offered to new borrowers. This is a key time to review your options. You can refix with the same lender, switch to their variable rate and negotiate a discount, or refinance to a different lender offering better terms. Contact your broker before your fixed rate expires to plan ahead.',
      },
      {
        q: 'Are there penalties for paying off a fixed rate loan early?',
        a: 'Yes, most fixed rate loans have break costs if you repay the loan in full or make repayments above the allowed limit during the fixed term. Break costs can be substantial and are calculated based on the remaining fixed term, your loan balance, and the difference between your fixed rate and the current wholesale rate. Always ask your lender for a break cost estimate before making decisions. Variable rate loans generally have no early repayment penalties.',
      },
    ]}
  >
    <p className="text-lg text-gray-700 leading-relaxed mb-8">
      Choosing between a fixed and variable interest rate is one of the most
      important decisions you will make when taking out a home loan. Both
      options have genuine advantages and trade-offs, and the right choice
      depends on your financial circumstances, risk tolerance, and plans for
      the future. This guide explains how each works so you can make an
      informed decision.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      How Fixed Rate Loans Work
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      With a fixed rate home loan, your interest rate is locked in for a
      specified period, commonly one, two, three, or five years. During this
      period, your repayments remain the same regardless of changes to the
      Reserve Bank of Australia (RBA) cash rate or broader market conditions.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Once the fixed term ends, your loan reverts to the lender's standard
      variable rate unless you choose to refix or refinance. Many borrowers
      use the fixed period as a tool for budgeting certainty, particularly
      during periods when rates are expected to rise.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      Advantages of Fixed Rates
    </h3>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>Repayment certainty.</strong> You know exactly what your
        repayments will be for the duration of the fixed term, making
        household budgeting straightforward.
      </li>
      <li className="leading-relaxed">
        <strong>Protection from rate rises.</strong> If the RBA raises the
        cash rate during your fixed term, your rate stays the same. This can
        be valuable if rates are expected to increase.
      </li>
      <li className="leading-relaxed">
        <strong>Peace of mind.</strong> Some borrowers simply prefer the
        certainty of knowing their repayments will not change, regardless
        of what happens in the economy.
      </li>
    </ul>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      Disadvantages of Fixed Rates
    </h3>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>Limited flexibility.</strong> Most fixed rate loans restrict
        extra repayments (typically capping them at $5,000 to $10,000 per
        year) and do not offer offset accounts.
      </li>
      <li className="leading-relaxed">
        <strong>Break costs.</strong> If you need to exit the fixed term
        early (for example, if you sell the property or want to refinance),
        you may face significant break costs.
      </li>
      <li className="leading-relaxed">
        <strong>Missing out on rate drops.</strong> If the RBA cuts rates
        during your fixed term, you will not benefit from the reduction.
      </li>
    </ul>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      How Variable Rate Loans Work
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      A variable rate loan has an interest rate that can change at any time.
      Lenders typically adjust variable rates in response to RBA cash rate
      decisions, but they are not obligated to pass on changes in full or at
      all. Your repayments will increase when rates rise and decrease when
      rates fall.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      Advantages of Variable Rates
    </h3>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>Full flexibility.</strong> Variable loans typically allow
        unlimited extra repayments, offset accounts, and redraw facilities.
        These features can help you pay off your loan faster and reduce
        total interest paid.
      </li>
      <li className="leading-relaxed">
        <strong>Benefit from rate cuts.</strong> When the RBA lowers the
        cash rate, your variable rate is likely to decrease, reducing your
        repayments.
      </li>
      <li className="leading-relaxed">
        <strong>No break costs.</strong> You can refinance or pay off the
        loan at any time without incurring break costs (though discharge
        fees may still apply).
      </li>
      <li className="leading-relaxed">
        <strong>Offset accounts.</strong> Offset accounts reduce the
        interest you pay by offsetting your loan balance against your
        savings. This is one of the most powerful features available on
        variable rate loans.
      </li>
    </ul>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      Disadvantages of Variable Rates
    </h3>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>Repayment uncertainty.</strong> Your repayments can increase
        if rates rise, which can strain your budget. A 1% rate increase on
        a $500,000 loan adds approximately $260 to your monthly repayment.
      </li>
      <li className="leading-relaxed">
        <strong>Budgeting difficulty.</strong> Fluctuating repayments make
        it harder to plan your monthly cash flow with precision.
      </li>
    </ul>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Split Loans: The Best of Both Worlds?
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      A split loan divides your mortgage into two portions: one fixed and
      one variable. This approach gives you partial protection from rate
      rises while maintaining some flexibility for extra repayments and
      offset account usage.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      For example, on a $600,000 loan, you might fix $400,000 for three
      years and keep $200,000 on a variable rate with an offset account.
      The fixed portion gives you repayment certainty on the majority of
      your debt, while the variable portion lets you park savings in offset
      and make extra repayments without restriction.
    </p>
    <p className="text-gray-700 leading-relaxed mb-6">
      The split ratio is entirely up to you and your broker. There is no
      "correct" split — it depends on how much certainty you want versus
      how much flexibility you need. Our brokers at{' '}
      <Link to="/contact" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        Manage Your Loans
      </Link>{' '}
      can model different split scenarios to show you the impact on
      repayments and total interest.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      How to Decide: Questions to Ask Yourself
    </h2>
    <div className="bg-gray-50 rounded-xl p-6 my-8 border border-gray-100">
      <ul className="space-y-4 text-gray-700">
        <li className="leading-relaxed">
          <strong>Do I value certainty or flexibility more?</strong> If
          knowing your exact repayments is important, lean towards fixed.
          If you want the ability to make extra repayments and use an
          offset account, lean towards variable.
        </li>
        <li className="leading-relaxed">
          <strong>What are my plans for the next few years?</strong> If you
          may sell, renovate, or make significant financial changes, a
          variable rate (or a short fixed term) offers more flexibility.
        </li>
        <li className="leading-relaxed">
          <strong>Can I handle repayment increases?</strong> If your budget
          is tight and a rate rise would cause genuine stress, a fixed rate
          provides a safety net.
        </li>
        <li className="leading-relaxed">
          <strong>Do I have significant savings?</strong> If you have
          substantial savings that could sit in an offset account, a
          variable rate may save you more in interest than the rate
          difference between fixed and variable.
        </li>
      </ul>
    </div>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Current Market Context
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      Interest rate decisions are influenced by the RBA's monetary policy,
      inflation trends, employment data, and global economic conditions.
      Fixed rates are priced based on where lenders expect rates to move in
      the future, so they are not always lower than variable rates — in
      fact, during periods when the market expects rates to fall, fixed
      rates may be higher than variable rates.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Rather than trying to predict rate movements, focus on choosing the
      loan structure that best matches your financial situation and risk
      tolerance. A mortgage broker can show you the current rates available
      across the market and help you make an informed decision.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      To compare your options,{' '}
      <Link to="/refinance" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        explore our refinancing services
      </Link>{' '}
      or{' '}
      <Link to="/contact" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        speak with one of our brokers
      </Link>.
      We compare rates from 80+ lenders to help you find the right
      structure for your needs. All loan documents are signed securely via
      SignAndGo, making the process convenient from start to finish.
    </p>
  </BlogArticleTemplate>
);

export default FixedVsVariable;
